import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalaliTs, localized } from "../lib/format";

interface Review {
  id: string;
  hallId: string;
  hallName: Record<string, string>;
  rating: number;
  text?: string;
  createdAt?: string | { _seconds: number };
  reply?: { text: string; byUid: string; at?: { _seconds: number } } | null;
}

export function ReviewsPage() {
  const { orgId } = useSession();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    api<{ items: Review[] }>(`/orgs/${orgId}/reviews`)
      .then((r) => setReviews(r.items))
      .catch((e) => setError(e.message));
  }, [orgId]);

  async function sendReply(review: Review) {
    const text = drafts[review.id]?.trim();
    if (!text) return;
    setBusyId(review.id);
    try {
      await api(`/orgs/${orgId}/reviews/${review.hallId}/${review.id}/reply`, {
        method: "POST",
        body: { text },
      });
      setReviews((prev) =>
        prev.map((r) =>
          r.id === review.id ? { ...r, reply: { text, byUid: "" } } : r,
        ),
      );
      setDrafts((prev) => ({ ...prev, [review.id]: "" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <Typography variant="h5" mb={2}>
        نظرات مشتریان
      </Typography>
      {error && <Typography color="error">{error}</Typography>}

      <Stack spacing={2}>
        {reviews.map((review) => (
          <Paper key={review.id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Rating value={review.rating} readOnly size="small" />
                <Chip size="small" label={localized(review.hallName)} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {formatJalaliTs(review.createdAt)}
              </Typography>
            </Stack>

            <Typography mt={1}>{review.text || "—"}</Typography>

            {review.reply ? (
              <Box
                mt={2}
                p={1.5}
                sx={{ bgcolor: "action.hover", borderRadius: 1, borderInlineStart: 3, borderColor: "primary.main" }}
              >
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  پاسخ شما
                </Typography>
                <Typography variant="body2">{review.reply.text}</Typography>
              </Box>
            ) : (
              <Stack direction="row" spacing={1} mt={2} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder="پاسخ خود را بنویسید…"
                  value={drafts[review.id] ?? ""}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [review.id]: e.target.value }))
                  }
                />
                <Button
                  variant="contained"
                  disabled={busyId === review.id || !drafts[review.id]?.trim()}
                  onClick={() => sendReply(review)}
                >
                  ارسال
                </Button>
              </Stack>
            )}
          </Paper>
        ))}

        {reviews.length === 0 && (
          <Paper variant="outlined">
            <Typography color="text.secondary" textAlign="center" py={4}>
              هنوز نظر منتشرشده‌ای ثبت نشده است
            </Typography>
          </Paper>
        )}
      </Stack>
    </>
  );
}
